/**
 * Utility functions for paginating chart data in PDF components
 */

/**
 * Paginates horizontal bar chart data with a maximum of 27 bars per page
 * @param {Array} data - Array of chart data objects
 * @param {number} maxBarsPerPage - Maximum number of bars per page (default: 27)
 * @returns {Array} Array of paginated data chunks
 */
export const paginateHorizontalBarData = (data, maxBarsPerPage = 27) => {
  if (!data || data.length === 0) return [];
  
  const pages = [];
  for (let i = 0; i < data.length; i += maxBarsPerPage) {
    pages.push(data.slice(i, i + maxBarsPerPage));
  }
  
  return pages;
};

/**
 * Calculates dynamic domain and ticks for paginated chart data
 * @param {Array} data - Chart data for current page
 * @param {Array} allData - Complete dataset for consistent scaling
 * @returns {Object} Object containing domain and ticks
 */
export const calculateChartDomain = (data, allData = null) => {
  const dataToAnalyze = allData || data;
  
  if (!dataToAnalyze || dataToAnalyze.length === 0) {
    return { domain: [0, 100], ticks: [0, 20, 40, 60, 80, 100] };
  }
  
  // Get all values from the dataset
  const allValues = dataToAnalyze.flatMap(item => {
    if (typeof item.value === 'number') {
      return [item.value];
    }
    // Handle stacked data
    if (item.participated !== undefined) {
      return [
        Number(item.participated || 0),
        Number(item.created || 0),
        Number(item.validated || 0)
      ];
    }
    return [0];
  }).filter(val => val > 0);
  
  const maxVal = allValues.length ? Math.max(...allValues, 0) : 0;
  const isSmall = maxVal <= 5;
  const roundedMax = isSmall
    ? Math.max(5, Math.ceil(maxVal / 5) * 5)
    : Math.ceil(maxVal / 10) * 10;
  
  const step = isSmall ? 1 : Math.max(1, Math.round(roundedMax / 5));
  const ticks = [];
  for (let t = 0; t <= roundedMax; t += step) {
    ticks.push(t);
  }
  
  return {
    domain: [0, roundedMax],
    ticks: ticks
  };
};

/**
 * Generates page titles for paginated charts
 * @param {string} baseTitle - Base title for the chart
 * @param {number} currentPage - Current page number (1-based)
 * @param {number} totalPages - Total number of pages
 * @returns {string} Formatted page title
 */
export const generatePageTitle = (baseTitle, currentPage, totalPages) => {
  if (totalPages <= 1) return baseTitle;
  return `${baseTitle} (Page ${currentPage} of ${totalPages})`;
};

/**
 * Calculates chart height based on number of bars for full PDF page utilization
 * @param {number} barCount - Number of bars in the chart
 * @param {number} barSize - Size of each bar (default: 16)
 * @param {number} maxHeight - Maximum available height for PDF page (default: 700)
 * @returns {number} Calculated height
 */
export const calculateChartHeight = (barCount, barSize = 16, maxHeight = 700) => {
  // Calculate height needed for all bars with proper spacing
  const calculatedHeight = barCount * (barSize + 8) + 120; // Add padding for axes and labels
  
  // Use the full available height or calculated height, whichever fits better
  return Math.min(maxHeight, Math.max(500, calculatedHeight));
};

/**
 * Calculates full page chart height for PDF pages
 * @param {number} barCount - Number of bars in the chart
 * @param {number} barSize - Size of each bar (default: 16)
 * @returns {number} Full page height for charts
 */
export const calculateFullPageChartHeight = (barCount, barSize = 16) => {
  // PDF page height minus header, title, and padding (approximately 700px available)
  const availableHeight = 700;
  
  // Calculate optimal height based on bar count
  const optimalHeight = barCount * (barSize + 10) + 150; // Extra spacing for PDF
  
  // Return full available height or optimal height, whichever is appropriate
  return Math.min(availableHeight, Math.max(500, optimalHeight));
};
